import {
  Button,
  Card,
  Descriptions,
  Divider,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd"
import Layout from "modules/layout/dashboard"
import { useTranslation } from "react-i18next"
import { useInvoice } from "../queries"
import { useInvoiceId, useMemberId } from "pages/member"
import { ReactComponent as PayementIcon } from "assets/icons/payment.svg"
import { ReactComponent as DownloadIcon } from "assets/icons/download.svg"

import Loader from "components/loader"
import { formatCurrency } from "utils/format-currency"
import { useMember } from "pages/member/queries"
import { useNavigate } from "react-router-dom"

import styles from "../styles.module.scss"
import moment from "moment"

const { Main } = Layout
const { Title, Text } = Typography
const Show: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const memberId = useMemberId()
  const member = useMember(memberId)
  const invoiceId = useInvoiceId()
  const { data, isLoading } = useInvoice(memberId!, invoiceId!)
  return (
    <>
      <Main.PageHeader
        breadcrumbs={[
          {
            title: member.data?.name,
            link: `/members/${member.data?.id}/events`,
          },
          {
            title: t("invoice.title"),
            link: `/members/${member.data?.id}/invoices`,
          },
          { title: data?.reference ?? data?.proforma_reference ?? "" },
        ]}
        title={
          <Title level={3}>{`${t("invoice.invoice")} - No: ${
            data?.reference ?? data?.proforma_reference ?? ""
          }`}</Title>
        }
        rightContent={
          <Space>
            <Button
              disabled={["paid", "recoverable", "canceled"].includes(
                data?.status!
              )}
              onClick={() => navigate("payment")}
            >
              <Space align="center">
                <PayementIcon />
                {t("invoice.paye")}
              </Space>
            </Button>
            <Button
              type="primary"
              onClick={() =>
                window.open(
                  new URL(
                    `operation/administration/members/${memberId}/invoices/${invoiceId}/export`,
                    import.meta.env.VITE_APP_API_URL
                  ).href
                )
              }
            >
              <Space align="center">
                <DownloadIcon />
                {t("global.download")}
              </Space>
            </Button>
          </Space>
        }
      />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Card>
            <Descriptions>
              <Descriptions.Item
                span={2}
                label={<Title level={5}>{t("invoice.number")}</Title>}
              >
                <Text>{data?.reference ?? data?.proforma_reference}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                span={2}
                label={<Title level={5}>{t("invoice.intended")}</Title>}
              >
                <Text>{data?.recipient.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                span={2}
                label={
                  <Title level={5}>
                    {t("global.created_at", { context: "female" })}
                  </Title>
                }
              >
                <Text>
                  {moment(data?.billing_date)
                    .utcOffset(0, false)
                    .format("YYYY-MM-DD")}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item
                span={2}
                label={<Title level={5}>{t("global.description")}</Title>}
              >
                <Text>{data?.notes}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                span={2}
                label={<Title level={5}>{t("invoice.due_date")}</Title>}
              >
                <Text>
                  {moment(data?.due_date)
                    .utcOffset(0, false)
                    .format("YYYY-MM-DD")}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item
                span={2}
                label={<Title level={5}>{t("global.status")}</Title>}
              >
                <Text>{data?.displayed_status}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Divider />
          <Table
            tableLayout="fixed"
            pagination={false}
            rowKey={"id"}
            dataSource={data?.items}
            footer={() => (
              <Descriptions className={styles.invoiceFooter} bordered>
                <Descriptions.Item
                  span={3}
                  label={<Title level={5}>{t("invoice.total_to_pay")}</Title>}
                >
                  <Text>{formatCurrency(data?.total_amount!)}</Text>
                </Descriptions.Item>
                <Descriptions.Item
                  span={3}
                  label={<Title level={5}>{t("invoice.payments_done")}</Title>}
                >
                  <Text>{formatCurrency(data?.total_paid!)}</Text>
                </Descriptions.Item>
                <Descriptions.Item
                  span={3}
                  style={{ backgroundColor: "rgba(0, 0, 0, 0.2)" }}
                  label={<Title level={5}>{t("invoice.balance_to_pay")}</Title>}
                >
                  <Text strong>
                    {formatCurrency(data?.total_amount! - data?.total_paid!)}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            )}
            columns={[
              {
                title: t<string>("global.created_at", { context: "female" }),
                dataIndex: "created_at",
                key: "created_at",
                width: 130,
                ellipsis: true,
                render: (value) => (
                  <Tooltip placement="bottomLeft" title={value}>
                    {moment(value).utcOffset(0, false).format("YYYY-MM-DD")}
                  </Tooltip>
                ),
              },
              {
                title: t<string>("global.description"),
                dataIndex: "name",
                key: "item",
                ellipsis: true,
                render: (value, record) => {
                  return (
                    <Tooltip placement="bottomLeft" title={value}>
                      {`${value} ${
                        record.description ? `- ${record.description}` : ""
                      }`}
                    </Tooltip>
                  )
                },
              },
              {
                title: t<string>("invoice.unit_price"),
                dataIndex: "unit_price",
                key: "unit_price",
                width: 150,
                ellipsis: true,
                align: "center",
                render: (value) => (
                  <Tooltip placement="bottomLeft" title={value}>
                    {value}
                  </Tooltip>
                ),
              },
              {
                title: t<string>("invoice.quantity"),
                dataIndex: "quantity",
                key: "quantity",
                width: 150,
                ellipsis: true,
                align: "center",
                render: (value) => (
                  <Tooltip placement="bottomLeft" title={value}>
                    {value}
                  </Tooltip>
                ),
              },
            ]}
          />
        </>
      )}
    </>
  )
}

export default Show
